import React from "react";
import { ShieldCheck, AlertTriangle, Info, CheckCircle2, Activity } from "lucide-react";
import { useI18n } from "@/i18n";

const DOT = {
  ok: "text-signal-success",
  warn: "text-yellow-400",
  info: "text-aurora-cyan",
};
const ICON = { ok: CheckCircle2, warn: AlertTriangle, info: Info };

const ScoreRing = ({ score, level }) => {
  const r = 34, c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  const stroke = level === "warn" ? "#facc15" : score >= 80 ? "#00E08A" : "#00E5FF";
  return (
    <div className="relative w-24 h-24 shrink-0">
      <svg viewBox="0 0 80 80" className="w-24 h-24 -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={stroke} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-2xl font-bold leading-none">{score}</span>
        <span className="font-mono text-[8px] uppercase tracking-widest text-zinc-500">/ 100</span>
      </div>
    </div>
  );
};

export const FileDiagnostics = ({ integrity }) => {
  const { t } = useI18n();
  if (!integrity) return null;
  const { checks = [], score = 0, verdict, verdict_code, level = "ok" } = integrity;

  const fmtSize = (bytes = 0) => {
    if (bytes >= 1048576) return t("diag.size.mb", { v: (bytes / 1048576).toFixed(2) });
    if (bytes >= 1024) return t("diag.size.kb", { v: (bytes / 1024).toFixed(1) });
    return t("diag.size.b", { v: bytes });
  };
  const labelText = (ch) => (ch.code ? t(`diag.checks.${ch.code}`) : ch.label);
  const detailText = (ch) => {
    if (!ch.detail_code) return ch.detail;
    if (ch.detail_code === "size") return fmtSize(ch.params?.bytes);
    return t(`diag.${ch.detail_code}`, ch.params || {});
  };
  const verdictText = verdict_code ? t(`diag.${verdict_code}`) : verdict;

  return (
    <div className="glass rounded-md p-6" data-testid="file-diagnostics">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-5 h-5 text-aurora-cyan" />
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight">{t("diag.title")}</h3>
        {integrity.prior_tuning === "possible" && (
          <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-yellow-400 bg-yellow-400/10 border border-yellow-400/30 px-2 py-0.5 rounded-sm">{t("diag.priorBadge")}</span>
        )}
      </div>

      <div className="flex items-center gap-5 mb-5">
        <ScoreRing score={score} level={level} />
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5 text-aurora-cyan" /> {t("diag.confidence")}
          </div>
          <p className={`text-sm ${level === "warn" ? "text-yellow-300" : "text-zinc-200"}`}>{verdictText}</p>
        </div>
      </div>

      <div className="space-y-2.5">
        {checks.map((ch, i) => {
          const I = ICON[ch.status] || Info;
          return (
            <div key={i} className="flex items-start gap-3" data-testid={`diag-check-${i}`}>
              <I className={`w-4 h-4 mt-0.5 shrink-0 ${DOT[ch.status] || DOT.info}`} />
              <div className="min-w-0 flex-1 flex justify-between gap-3 border-b border-white/5 pb-2.5">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">{labelText(ch)}</span>
                <span className="text-sm text-zinc-300 text-right">{detailText(ch)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
